import type {
  CommandGroupWithActions,
  CommandItem,
} from "@v1/components/common/designSystem/utils/commands";
import type {
  HotkeyIndicatorCharacter,
  HotkeyMetaKey,
} from "@v1/components/common/designSystem/utils/hotkeys";

import * as React from "react";

import { commandItemHasChildren } from "@v1/components/common/designSystem/utils/commands";
import { HotkeyMetaKeyToLabel } from "@v1/components/common/designSystem/utils/hotkeys";
import { isMacintosh } from "@v1/components/common/designSystem/utils/platform";
import twMerge from "@v1/components/common/designSystem/utils/twMerge";
import {
  LEGACY_COMMAND_GROUPS,
  useAllDynamicCommandMenuItems,
} from "@v2/hooks/useDynamicCommandMenuItems";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { Command } from "cmdk";
import { Plus } from "lucide-react";
import { useLocation } from "react-router-dom";
import { exhaustiveSwitch, hasOwnProperty } from "replo-utils/lib/misc";

/**
 * State that can be pushed onto the command menu stack. Default means the default
 * command menu items, depending on editor context. Custom means we're viewing a child
 * command group with specific items.
 */
type CommandMenuState =
  | "default"
  | { type: "custom"; commandGroup: CommandGroupWithActions };

const isCustomCommandMenuState = (
  state: CommandMenuState,
): state is { type: "custom"; commandGroup: CommandGroupWithActions } => {
  return hasOwnProperty(state, "type") && state.type === "custom";
};

export function CommandMenu() {
  const location = useLocation();
  const defaultCommandMenuItems = useCommandMenuItems();
  const commandMenuStack = useCommandMenuStack();
  const [searchTerm, setSearchTerm] = React.useState("");
  const logEvent = useLogAnalytics();

  // NOTE (Ben, 2026-05-12): The agent surface ships its own Cmd+K launcher
  // (AppLauncher in the replo-agent app).
  // Short-circuit before any state / refs / listeners so the v1 menu does
  // not mount on /agent and the two surfaces never compete for the hotkey.
  const isAgentRoute = location.pathname.startsWith("/agent");

  // Note (Noah, 2025-01-22): Listener for cmd-k, to open the menu from anywhere
  React.useEffect(() => {
    if (isAgentRoute) {
      return;
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (commandMenuStack.isEmpty) {
          logEvent("editor.cmdk.open");
          commandMenuStack.push("default");
        } else {
          logEvent("editor.cmdk.close", { interaction: "cmd-k" });
          commandMenuStack.clear();
        }
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [commandMenuStack, isAgentRoute, logEvent]);

  if (isAgentRoute) {
    return null;
  }

  const commandMenuItems =
    commandMenuStack.currentState &&
    isCustomCommandMenuState(commandMenuStack.currentState)
      ? [commandMenuStack.currentState.commandGroup]
      : defaultCommandMenuItems;

  const handleItemSelect = (item: CommandItem, key: string) => {
    logEvent("editor.cmdk.select", { label: item.label });
    if (commandItemHasChildren(item)) {
      commandMenuStack.push({
        type: "custom",
        commandGroup: {
          id: key,
          heading: item.label,
          items: item.children,
        },
      });
      setSearchTerm("");
    } else if ("onSelect" in item) {
      item.onSelect();
      commandMenuStack.clear();
      setSearchTerm("");
    }
  };

  return (
    <Command.Dialog
      open={!commandMenuStack.isEmpty}
      onOpenChange={(isOpen) => {
        if (isOpen) {
          commandMenuStack.push("default");
        } else {
          logEvent("editor.cmdk.close");
          commandMenuStack.clear();
        }
      }}
      label="Replo Command Menu"
      onKeyDown={(e) => {
        // Backspace goes to previous page when search is empty
        const isEscape = e.key === "Escape";
        const isBackspace = e.key === "Backspace";
        const shouldPopDueToBackspace = isBackspace && !searchTerm;
        if (isEscape || shouldPopDueToBackspace) {
          e.preventDefault();

          if (commandMenuStack.length > 1) {
            logEvent("editor.cmdk.back", {
              interaction: e.key === "Escape" ? "esc" : "backspace",
            });
          }

          if (
            commandMenuStack.currentState &&
            isCustomCommandMenuState(commandMenuStack.currentState)
          ) {
            // Note (Noah, 2025-01-22): Go back one stack frame by popping
            // the last item off the stack
            commandMenuStack.pop();
          }
        }

        if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "Enter") {
          e.stopPropagation();
        }
      }}
    >
      <Command.Input
        value={searchTerm}
        onValueChange={setSearchTerm}
        placeholder="Type a command or search"
      />

      <Command.List>
        <Command.Empty>No results found.</Command.Empty>
        {commandMenuItems.map((group) => (
          <Command.Group key={group.heading} heading={group.heading}>
            {Object.entries(group.items)
              .filter(([, item]) => !item.isDisabled)
              .map(([key, item]) => {
                return (
                  <React.Fragment key={key}>
                    <SingleCommandListItem
                      item={item}
                      onSelect={() => handleItemSelect(item, key)}
                    />
                    {/* Note (Noah, 2025-01-19): If we're an item that has children,
                    also add in our children as items so that users can directly find
                    the children without having click us, if they want. This is how
                    Linear's cmd-k works.*/}
                    {commandItemHasChildren(item) &&
                      Object.entries(item.children).map(([key, child]) => (
                        <SingleCommandListItem
                          key={`child-${key}`}
                          item={child}
                          onSelect={() => handleItemSelect(child, key)}
                        />
                      ))}
                  </React.Fragment>
                );
              })}
          </Command.Group>
        ))}
      </Command.List>
    </Command.Dialog>
  );
}

// Note (Noah, 2025-01-22): We use a stack here since there can be multiple
// levels of nested child command groups. Backspace pops off the stack, and the
// last item of the stack is the "current" one. Empty stack means the cmd-k modal
// is not shown at all
function useCommandMenuStack() {
  const location = useLocation();
  const [commandMenuStack, setCommandMenuStack] = React.useState<
    CommandMenuState[]
  >([]);

  const push = React.useCallback(
    (state: CommandMenuState) => {
      setCommandMenuStack([...commandMenuStack, state]);
    },
    [commandMenuStack],
  );

  const pop = React.useCallback(() => {
    setCommandMenuStack(commandMenuStack.slice(0, -1));
  }, [commandMenuStack]);

  const clear = React.useCallback(() => {
    setCommandMenuStack([]);
  }, []);

  // If the location changes,  clear the stack
  React.useEffect(() => {
    if (location) {
      clear();
    }
  }, [location, clear]);

  return {
    currentState: commandMenuStack[commandMenuStack.length - 1],
    isEmpty: commandMenuStack.length === 0,
    length: commandMenuStack.length,
    push,
    pop,
    clear,
  };
}

/**
 * Renders a single <Command.Item> for the given item. If the item has children,
 * this renders only the parent (children should be rendered separately).
 */
function SingleCommandListItem({
  item,
  onSelect,
}: {
  item: CommandItem;
  onSelect: () => void;
}) {
  return (
    <Command.Item onSelect={onSelect}>
      <div className="flex">
        <CommandIcon item={item} />
      </div>
      <div className="label">{item.label}</div>
      {item.hotkey && (
        <div className="text-muted ml-auto">{getHotkeyLabel(item.hotkey)}</div>
      )}
    </Command.Item>
  );
}

function CommandIcon({ item }: { item: CommandItem }) {
  if (item.icon) {
    return exhaustiveSwitch(item.icon)({
      iconComponent: (icon) => <icon.component size={16} />,
      publishedStatus: ({ isPublished }) => (
        <div className="flex h-[16px] w-[16px] items-center justify-center">
          <div
            className={twMerge(
              "h-[8px] w-[8px] rounded-full",
              isPublished ? "bg-emerald-soft" : "bg-surface",
            )}
          />
        </div>
      ),
      create: () => <Plus size={14} />,
      custom: ({ content }) => {
        return <>{content}</>;
      },
    });
  }

  return <div className="bg-surface h-[16px] w-[16px] rounded-full" />;
}

/**
 * Builds command menu groups from dynamically registered items.
 * Items are registered via useDynamicCommandMenuItem hook.
 */
function useCommandMenuItems(): CommandGroupWithActions[] {
  const dynamicMenuItems = useAllDynamicCommandMenuItems();

  // Group dynamic items by their group ID
  const groupedItems = React.useMemo(() => {
    const groups: Record<string, CommandGroupWithActions["items"]> = {};

    Object.entries(dynamicMenuItems).forEach(([key, item]) => {
      if (!groups[item.group]) {
        groups[item.group] = {};
      }
      // TypeScript doesn't recognize that we just created groups[item.group] above
      groups[item.group]![key] = item;
    });

    return groups;
  }, [dynamicMenuItems]);

  return React.useMemo(() => {
    const menuItems: CommandGroupWithActions[] = [];

    LEGACY_COMMAND_GROUPS.forEach(({ id, heading }) => {
      if (groupedItems[id] && Object.keys(groupedItems[id]).length > 0) {
        menuItems.push({
          id,
          heading,
          items: groupedItems[id],
        });
      }
    });

    return menuItems;
  }, [groupedItems]);
}

const getHotkeyLabel = (hotkey: HotkeyIndicatorCharacter[]) => {
  return hotkey.map((character) => {
    const metaKeyLabel = HotkeyMetaKeyToLabel[character as HotkeyMetaKey];
    if (!metaKeyLabel) {
      return character.toUpperCase();
    }

    if (typeof metaKeyLabel === "string") {
      return metaKeyLabel;
    }

    return isMacintosh() ? metaKeyLabel.mac : metaKeyLabel.windows;
  });
};
